import { useState } from "react";
import {
  useUsers,
  useSetUserTier,
  useSetUserBudget,
} from "@/api/hooks/use-users";
import type { DashboardUserPolicy } from "@/api/schemas";
import { formatUsd } from "@/lib/format";
import { toUserMessage } from "@/lib/errors";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";

const sectionCls = "rounded-lg border border-line-subtle bg-surface p-5 space-y-4";
const sectionTitle = "text-sm font-medium text-fg-muted uppercase tracking-wider";
const noteCls = "text-xs text-fg-muted italic";
const inputCls =
  "w-32 rounded-md border border-line bg-canvas px-2 py-1 text-sm text-fg placeholder:text-fg-faint focus:border-electric-blue focus:outline-none";
const btnSmall =
  "rounded-md border border-electric-blue/40 px-2.5 py-1 text-xs text-electric-blue hover:bg-electric-blue/10 disabled:opacity-50 transition-colors";

// Per-user tier + budget controls for the Governance tab. Renders nothing
// until the tenant has 2+ end-users — a single-user (or no-user) tenant has
// nothing meaningful to govern per-user. Independent of the page-top
// time/user filters by design.
export function PerUserPolicies({ tenantId }: { tenantId: string }) {
  const usersQ = useUsers(tenantId);

  if (usersQ.isLoading) {
    return (
      <section className={sectionCls}>
        <h2 className={sectionTitle}>Per-user policies</h2>
        <LoadingSkeleton rows={3} />
      </section>
    );
  }

  const users = usersQ.data?.users ?? [];
  if (usersQ.isError || users.length < 2) {
    // <2 users: hide entirely. Error: stay silent rather than break the page.
    return null;
  }

  const ceiling = usersQ.data!.tenant_effective_tier;
  const collaborativeAllowed = ceiling === "collaborative";
  const tierEditable = usersQ.data!.tier_editable;

  return (
    <section className={sectionCls}>
      <h2 className={sectionTitle}>Per-user policies</h2>
      <p className={noteCls}>
        Override the {tierEditable ? "data tier and " : ""}monthly budget for individual end-users
        (identified by the <span className="font-mono not-italic">X-User-Id</span>{" "}
        header). Unset values inherit your account defaults.
      </p>
      {tierEditable && !collaborativeAllowed && (
        <p className={noteCls}>
          Your account is on the Privacy tier, so users cannot be set to
          Collaborative. Switch your account to Collaborative first.
        </p>
      )}
      <div className="overflow-x-auto rounded-lg border border-line-subtle">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line-subtle text-left text-xs font-medium text-fg-muted uppercase tracking-wider">
              <th className="px-4 py-2.5">User</th>
              {tierEditable && <th className="px-4 py-2.5">Data tier</th>}
              <th className="px-4 py-2.5">Monthly budget (USD)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line-subtle">
            {users.map((u) => (
              <UserPolicyRow
                key={u.external_user_id}
                tenantId={tenantId}
                user={u}
                collaborativeAllowed={collaborativeAllowed}
                tierEditable={tierEditable}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function UserPolicyRow({
  tenantId,
  user,
  collaborativeAllowed,
  tierEditable,
}: {
  tenantId: string;
  user: DashboardUserPolicy;
  collaborativeAllowed: boolean;
  tierEditable: boolean;
}) {
  const setTier = useSetUserTier();
  const setBudget = useSetUserBudget();
  const [budgetDraft, setBudgetDraft] = useState(user.monthly_budget_usd ?? "");
  const [feedback, setFeedback] = useState<{ type: "ok" | "err"; msg: string } | null>(null);

  function flash(type: "ok" | "err", msg: string) {
    setFeedback({ type, msg });
    setTimeout(() => setFeedback(null), 4000);
  }

  function handleTierChange(next: string) {
    if (next !== "privacy" && next !== "collaborative") return;
    setTier.mutate(
      { tenantId, externalUserId: user.external_user_id, dataTier: next },
      {
        onSuccess: () => flash("ok", "Tier updated"),
        onError: (err) => flash("err", toUserMessage(err)),
      },
    );
  }

  function handleBudgetSave() {
    const trimmed = budgetDraft.trim();
    const value = trimmed === "" ? null : Number.parseFloat(trimmed);
    if (value !== null && (Number.isNaN(value) || value < 0)) {
      flash("err", "Enter a valid amount");
      return;
    }
    setBudget.mutate(
      { tenantId, externalUserId: user.external_user_id, monthlyBudgetUsd: value },
      {
        onSuccess: () => flash("ok", "Budget updated"),
        onError: (err) => flash("err", toUserMessage(err)),
      },
    );
  }

  const budgetDirty = budgetDraft.trim() !== (user.monthly_budget_usd ?? "");

  return (
    <tr className="text-fg">
      <td className="px-4 py-2.5 font-mono">{user.external_user_id}</td>
      {tierEditable && (
        <td className="px-4 py-2.5">
          <select
            value={user.effective_data_tier}
            onChange={(e) => handleTierChange(e.target.value)}
            disabled={setTier.isPending}
            aria-label={`Data tier for ${user.external_user_id}`}
            className="rounded-md border border-line bg-canvas px-2 py-1 text-sm text-fg focus:border-electric-blue focus:outline-none disabled:opacity-50"
          >
            <option value="privacy">Privacy</option>
            <option value="collaborative" disabled={!collaborativeAllowed}>
              Collaborative
            </option>
          </select>
        </td>
      )}
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            step="0.01"
            value={budgetDraft}
            onChange={(e) => setBudgetDraft(e.target.value)}
            placeholder="Inherit"
            className={inputCls}
          />
          <button
            type="button"
            onClick={handleBudgetSave}
            disabled={setBudget.isPending || !budgetDirty}
            className={btnSmall}
          >
            {setBudget.isPending ? "..." : "Save"}
          </button>
          {feedback && (
            <span
              role={feedback.type === "ok" ? "status" : "alert"}
              className={`text-xs ${feedback.type === "ok" ? "text-safe-green" : "text-block-red"}`}
            >
              {feedback.msg}
            </span>
          )}
          {!feedback && user.monthly_budget_usd && (
            <span className="text-xs text-fg-muted">
              now {formatUsd(user.monthly_budget_usd)}
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}
